import { useCallback, useEffect, useState } from "react";
import { adminFetchJson, normalizeArrayPayload } from "./adminApi";

function downloadCSV(data) {
  if (!Array.isArray(data) || !data.length) return;

  const headers = Object.keys(data[0]);
  const rows = data.map((row) =>
    headers
      .map((h) => {
        const val = row[h] ?? "";
        return typeof val === "string" && /[",\n]/.test(val) ? `"${val.replace(/"/g, '""')}"` : val;
      })
      .join(",")
  );

  const csvContent = [headers.join(","), ...rows].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "contact_submissions.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function formatMessage(message) {
  try {
    const parsed = JSON.parse(message || "");
    return JSON.stringify(parsed, null, 2);
  } catch {
    return message || "";
  }
}

export default function ContactSubmissionsWidget() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [deletingId, setDeletingId] = useState(null);

  const loadSubmissions = useCallback(() => {
    setLoading(true);
    setMessage("");

    return adminFetchJson("/api/contact_submissions.php")
      .then((data) => {
        setSubmissions(normalizeArrayPayload(data, ["submissions", "rows", "data"]));
      })
      .catch((err) => {
        if (err.authRequired) return;
        setMessage(err.message || "Unable to load contact submissions.");
        setSubmissions([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setMessage("");

    adminFetchJson("/api/contact_submissions.php")
      .then((data) => {
        if (!active) return;
        setSubmissions(normalizeArrayPayload(data, ["submissions", "rows", "data"]));
      })
      .catch((err) => {
        if (!active || err.authRequired) return;
        setMessage(err.message || "Unable to load contact submissions.");
        setSubmissions([]);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const deleteSubmission = async (sub) => {
    const id = Number(sub.id || 0);
    if (!id) {
      setMessage("This submission is missing an ID, so it cannot be deleted safely.");
      return;
    }
    if (!window.confirm(`Delete the contact submission from ${sub.name || sub.email || "this visitor"}?`)) return;

    setDeletingId(id);
    setMessage("");
    try {
      const data = await adminFetchJson(`/api/contact_submissions.php?id=${encodeURIComponent(id)}`, {
        method: "DELETE",
      });
      if (!data.success) throw new Error(data.error || "Delete failed.");
      setSubmissions((rows) => rows.filter((row) => Number(row.id) !== id));
      setMessage("Contact submission deleted.");
    } catch (err) {
      if (!err.authRequired) setMessage(err.message || "Unable to delete contact submission.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="bg-white text-gray-900 rounded-2xl shadow p-6 w-full">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Contact Submissions</h2>
          <p className="mt-1 text-xs text-gray-500">Review new messages and delete old or spam submissions when they are no longer needed.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            className="px-4 py-2 border border-gray-300 hover:bg-gray-50 disabled:opacity-50 text-gray-700 rounded"
            disabled={loading}
            onClick={loadSubmissions}
            type="button"
          >
            Refresh
          </button>
          <button
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded"
            disabled={!submissions.length}
            onClick={() => downloadCSV(submissions)}
            type="button"
          >
            Download CSV
          </button>
        </div>
      </div>

      {message && <div className={`mb-4 rounded-lg border px-4 py-3 text-sm ${message.includes("deleted") ? "border-green-200 bg-green-50 text-green-700" : "border-red-200 bg-red-50 text-red-700"}`}>{message}</div>}
      {loading && <p className="text-sm text-gray-500">Loading contact submissions…</p>}

      <ul className="space-y-6 text-sm max-h-[500px] overflow-y-auto pr-2">
        {!loading && submissions.length === 0 && <li className="text-gray-500">No contact submissions found.</li>}
        {submissions.map((sub, i) => {
          const id = Number(sub.id || 0);
          return (
            <li key={id || `${sub.email || "submission"}-${sub.submitted_at || i}`} className="border-b pb-4">
              <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
                <div>
                  <p className="font-medium text-gray-900">{sub.name || "Unknown name"}</p>
                  <p className="text-sm text-gray-700">{sub.email || "No email"} • {sub.phone || "No phone"}</p>
                  <p className="text-xs text-gray-500 mb-1">
                    IP: {sub.ip_address || "—"} • {sub.submitted_at ? new Date(sub.submitted_at).toLocaleString() : "—"}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={!id || deletingId === id}
                  onClick={() => deleteSubmission(sub)}
                  className="self-start rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {deletingId === id ? "Deleting..." : "Delete"}
                </button>
              </div>
              <pre className="mt-3 bg-gray-50 border p-3 rounded text-xs whitespace-pre-wrap">
                {formatMessage(sub.message)}
              </pre>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
